import React, { useState } from 'react'
import Spinner from '../Spinner'
import { useSelector } from 'react-redux'
import constants from '../../constants'

// variants: contained, outlined, text

const Button = ({
  children,
  variant = 'contained',
  fullWidth = false,
  onClick,
  disabled = false,
  loading = false,
  size = 'medium',
  color = '',
  isFlatButton: isFlatButtonProp,
}) => {
  const [isPressed, setIsPressed] = useState(false)
  const isContained = variant === 'contained'
  const { theme } = useSelector((state) => state.theme)
  const isFlatButton =
    isFlatButtonProp ?? theme?.currentTheme === constants.CARE_PROGRAM_TYPE.GLP

  const buttonBaseClass =
    'z-0 w-full flex items-center justify-center gap-2 rounded-lg overflow-hidden transition-all duration-300 ease-in-out font-semibold'

  const buttonVariantClass = {
    contained: 'bg-brand-green',
    outlined: 'bg-white border border-brand-green text-brand-green',
    text: 'bg-white text-brand-green',
  }[variant]

  const buttonBottomBarBaseClass =
    '-z-10 w-full -mt-[12px] bg-brand-green-dark transition-all duration-300 ease-in-out transform origin-bottom overflow-hidden'

  const buttonBottomBarVariantClass = {
    contained: 'bg-brand-green-dark',
    outlined: 'bg-brand-green-light border border-brand-green',
    text: 'bg-transparent',
  }[variant]

  const buttonSizeClass = {
    xSmall: {
      pressed: 'h-[12px]',
      default: 'h-[17px]',
      padding: 'px-2 py-1',
      height: 'h-[41px]',
      fontSize: 'text-[14px]',
    },
    small: {
      pressed: 'h-[12px]',
      default: 'h-[20px]',
      padding: 'px-2 py-1',
      height: 'h-[48px]',
      fontSize: 'text-[14px]',
    },
    medium: {
      pressed: 'h-[12px]',
      default: 'h-[24px]',
      padding: 'px-4 py-2',
      height: 'h-[54px]',
      fontSize: 'text-[16px]',
    },
    large: {
      pressed: 'h-[12px]',
      default: 'h-[32px]',
      padding: 'px-6 py-3',
      height: 'h-[66px]',
      fontSize: 'text-[18px]',
    },
  }[size]

  const handleMouseDown = () => {
    if (!isFlatButton) {
      setIsPressed(true)
    }
  }

  const handleMouseUp = () => {
    if (isFlatButton) return

    setTimeout(() => {
      setIsPressed(false)
      setTimeout(() => {
        onClick && onClick()
      }, 100)
    }, 200)
  }

  return (
    <button
      className={`flex flex-col items-end justify-end relative rounded-lg text-white overflow-hidden z-0 ${
        fullWidth ? 'w-full' : ''
      } ${disabled ? 'opacity-60' : ''} ${buttonSizeClass.height}`}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={() => setIsPressed(false)}
      disabled={disabled}
      onClick={isFlatButton ? onClick : undefined}
    >
      <div
        className={`${buttonBaseClass} ${buttonVariantClass} ${buttonSizeClass.padding} ${buttonSizeClass.height} ${buttonSizeClass.fontSize}`}
        style={
          isContained
            ? { backgroundColor: color }
            : { borderColor: color, color: color }
        }
      >
        {loading ? (
          <div className="flex items-center gap-2">
            <Spinner color={variant === 'contained' ? '#FFF' : '#50B55D'} />
            Please wait...
          </div>
        ) : (
          children
        )}
      </div>

      {!isFlatButton && (
        <div
          className={`${buttonBottomBarBaseClass} ${buttonBottomBarVariantClass} ${
            isPressed ? buttonSizeClass.pressed : buttonSizeClass.default
          }`}
        />
      )}
    </button>
  )
}

export default Button
